import {
  Alert,
  Button,
  Code,
  DialogActionTrigger,
  DialogTitle,
  Flex,
  IconButton,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { FaCopy, FaEye, FaEyeSlash, FaPlus } from "react-icons/fa"

import type {
  OrganizationCreate,
  OrganizationPublic,
  UserPublic,
} from "@/client"
import { OrganizationsService } from "@/client"
import type { ApiError } from "@/client/core/ApiError"
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Field } from "@/components/ui/field"
import { Toaster, toaster } from "@/components/ui/toaster"
import useCustomToast from "@/hooks/useCustomToast"
import { handleError } from "@/utils"

function AddOrganization() {
  const [isOpen, setIsOpen] = useState(false)
  const [createdOrg, setCreatedOrg] = useState<OrganizationPublic | null>(null)
  const [showAppId, setShowAppId] = useState(false)
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()
  const currentUser = queryClient.getQueryData<UserPublic>(["currentUser"])

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid, isSubmitting },
  } = useForm<OrganizationCreate>({
    mode: "onBlur",
    defaultValues: {
      name: "",
      slug: "",
      settings: {},
    },
  })

  const mutation = useMutation({
    mutationFn: (data: OrganizationCreate) =>
      OrganizationsService.createOrganization({ requestBody: data }),
    onSuccess: (data: OrganizationPublic) => {
      showSuccessToast("Organization created successfully")
      setCreatedOrg(data)
      // Don't reset or close yet - show the keys first
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] })
    },
  })

  const onSubmit = (data: OrganizationCreate) => {
    mutation.mutate(data)
  }

  const handleClose = () => {
    setIsOpen(false)
    setCreatedOrg(null)
    setShowAppId(false)
    reset()
  }

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    toaster.create({
      title: `${label} copied`,
      description: "Copied to clipboard",
      type: "success",
      duration: 2000,
    })
  }

  const maskKey = (key: string, show: boolean) => {
    if (show) return key
    return "•".repeat(Math.min(key.length, 40))
  }

  // Only superadmins can create organizations
  if (!currentUser?.is_superuser) {
    return null
  }

  return (
    <>
      <Toaster />
      <DialogRoot
        size={{ base: "xs", md: createdOrg ? "xl" : "md" }}
        placement="center"
        open={isOpen}
        onOpenChange={({ open }) => {
          if (open) {
            setIsOpen(true)
          } else {
            handleClose()
          }
        }}
      >
        <DialogTrigger asChild>
          <Button value="add-organization" my={4}>
            <FaPlus fontSize="16px" />
            Add Organization
          </Button>
        </DialogTrigger>
        <DialogContent>
          {!createdOrg ? (
            // Creation Form
            <form onSubmit={handleSubmit(onSubmit)}>
              <DialogHeader>
                <DialogTitle>Add Organization</DialogTitle>
              </DialogHeader>
              <DialogBody>
                <VStack gap={4}>
                  <Field
                    required
                    invalid={!!errors.name}
                    errorText={errors.name?.message}
                    label="Name"
                  >
                    <Input
                      id="name"
                      {...register("name", {
                        required: "Name is required",
                      })}
                      placeholder="Organization Name"
                      type="text"
                    />
                  </Field>

                  <Field
                    required
                    invalid={!!errors.slug}
                    errorText={errors.slug?.message}
                    label="Slug"
                  >
                    <Input
                      id="slug"
                      {...register("slug", {
                        required: "Slug is required",
                        pattern: {
                          value: /^[a-z0-9-]+$/,
                          message:
                            "Slug can only contain lowercase letters, numbers, and hyphens",
                        },
                      })}
                      placeholder="organization-slug"
                      type="text"
                    />
                  </Field>
                </VStack>
              </DialogBody>

              <DialogFooter gap={2}>
                <DialogActionTrigger asChild>
                  <Button
                    variant="subtle"
                    colorPalette="gray"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </DialogActionTrigger>
                <Button
                  variant="solid"
                  type="submit"
                  disabled={!isValid}
                  loading={isSubmitting}
                >
                  Create
                </Button>
              </DialogFooter>
            </form>
          ) : (
            // Success View with App ID
            <>
              <DialogHeader>
                <DialogTitle>Organization Created Successfully!</DialogTitle>
              </DialogHeader>
              <DialogBody>
                <VStack gap={6} align="stretch">
                  <Alert.Root status="info">
                    <Alert.Indicator />
                    <Alert.Content>
                      <Alert.Title>
                        Next Step: Generate a Secret Key
                      </Alert.Title>
                      <Alert.Description>
                        The organization&apos;s secret key is not shown here for
                        security. Use the API Keys action in the organization
                        list to generate and securely store a secret key when
                        you&apos;re ready to embed tests.
                      </Alert.Description>
                    </Alert.Content>
                  </Alert.Root>

                  <VStack gap={2} align="stretch">
                    <Text fontWeight="bold">Organization Name</Text>
                    <Code p={2} borderRadius="md">
                      {createdOrg.name}
                    </Code>
                  </VStack>

                  <VStack gap={2} align="stretch">
                    <Text fontWeight="bold">App ID</Text>
                    <Flex gap={2} align="center">
                      <Input
                        value={maskKey(createdOrg.app_id, showAppId)}
                        readOnly
                        fontFamily="mono"
                        fontSize="sm"
                      />
                      <IconButton
                        aria-label={showAppId ? "Hide App ID" : "Show App ID"}
                        onClick={() => setShowAppId(!showAppId)}
                        variant="ghost"
                        size="sm"
                      >
                        {showAppId ? <FaEyeSlash /> : <FaEye />}
                      </IconButton>
                      <IconButton
                        aria-label="Copy App ID"
                        onClick={() =>
                          copyToClipboard(createdOrg.app_id, "App ID")
                        }
                        variant="ghost"
                        size="sm"
                      >
                        <FaCopy />
                      </IconButton>
                    </Flex>
                  </VStack>
                </VStack>
              </DialogBody>

              <DialogFooter>
                <Button variant="solid" onClick={handleClose}>
                  Done
                </Button>
              </DialogFooter>
            </>
          )}
          <DialogCloseTrigger />
        </DialogContent>
      </DialogRoot>
    </>
  )
}

export default AddOrganization
